import { useRef, useState } from "react";
import { api, errMsg, fmtMoney } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { toast } from "sonner";
import { Camera, Upload, Loader2, CheckCircle2, X, DollarSign } from "lucide-react";

const METHODS = [
  { id: "venmo",  label: "Venmo",  color: "#3D95CE" },
  { id: "zelle",  label: "Zelle",  color: "#6D1AC5" },
  { id: "cash",   label: "Cash",   color: "#10B981" },
  { id: "check",  label: "Check",  color: "#F59E0B" },
  { id: "paypal", label: "PayPal", color: "#003087" },
  { id: "other",  label: "Other",  color: "#94A3B8" },
];

/**
 * Manual receipt uploader — iOS-style bottom sheet. Big touch targets,
 * subtle spring feel, single-action confirm.
 */
export function ReceiptUploader({ open, onOpenChange, missionId, missionNumber, defaultAmount = 0, onDone }) {
  const fileRef = useRef(null);
  const [photo, setPhoto] = useState("");
  const [method, setMethod] = useState("venmo");
  const [amount, setAmount] = useState(defaultAmount ? String(defaultAmount) : "");
  const [kind, setKind] = useState("full");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const pickFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const r = new FileReader();
    r.onload = () => setPhoto(String(r.result || ""));
    r.readAsDataURL(f);
  };

  const reset = () => { setPhoto(""); setAmount(defaultAmount ? String(defaultAmount) : ""); setNote(""); setMethod("venmo"); setKind("full"); };

  const submit = async () => {
    if (!photo) { toast.error("Take or choose a photo of the receipt"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/ops/missions/${missionId}/manual-receipt`, {
        photo, method, amount: Number(amount) || 0, kind, note,
      });
      toast.success("Receipt saved — mission marked paid");
      onDone?.(data);
      reset();
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-3xl p-0 border-0 bg-background max-h-[92vh] overflow-y-auto"
        data-testid="receipt-uploader-sheet">
        {/* iOS-style grab handle */}
        <div className="w-12 h-1.5 rounded-full bg-muted-foreground/40 mx-auto mt-2.5" />
        <SheetHeader className="px-6 pt-4 pb-2">
          <SheetTitle className="font-heading text-2xl font-extrabold text-center">Upload Receipt</SheetTitle>
          <p className="text-center text-sm text-muted-foreground">
            Manually mark <span className="font-mono-cc text-foreground">{missionNumber}</span> as paid with a proof photo.
          </p>
        </SheetHeader>

        <div className="p-5 space-y-5">
          {/* Photo dropzone — big, iOS-flavored */}
          <button onClick={() => fileRef.current?.click()} type="button" data-testid="receipt-photo-picker"
            className={`relative w-full rounded-3xl border-2 border-dashed transition-all duration-300 overflow-hidden ${
              photo ? "border-emerald-500/50 bg-emerald-500/5 aspect-[4/5]" : "border-border hover:border-[var(--cc-accent)]/60 py-16"
            }`}>
            {photo ? (
              <>
                <img src={photo} alt="Receipt preview" className="w-full h-full object-cover" />
                <span className="absolute top-3 right-3 rounded-full bg-black/60 backdrop-blur-md px-3 py-1.5 text-xs font-semibold text-white flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Ready
                </span>
              </>
            ) : (
              <div className="flex flex-col items-center gap-3 text-muted-foreground">
                <span className="w-16 h-16 rounded-full bg-[var(--cc-accent)]/10 border border-[var(--cc-accent)]/30 flex items-center justify-center">
                  <Camera className="w-7 h-7 text-[var(--cc-accent)]" />
                </span>
                <div>
                  <p className="font-heading font-bold text-lg text-foreground">Take a photo</p>
                  <p className="text-xs">or choose from library — JPG/PNG, up to 3&nbsp;MB</p>
                </div>
              </div>
            )}
            <input ref={fileRef} type="file" accept="image/*" capture="environment" hidden onChange={pickFile}
              data-testid="receipt-file-input" />
          </button>
          {photo && (
            <button onClick={() => setPhoto("")} className="w-full text-xs text-muted-foreground py-1" data-testid="receipt-clear-photo">
              <X className="w-3.5 h-3.5 inline mr-1" /> Clear photo
            </button>
          )}

          {/* Method — iOS segmented look */}
          <div>
            <p className="text-xs font-mono-cc uppercase tracking-widest text-muted-foreground mb-2">Payment method</p>
            <div className="grid grid-cols-3 gap-2">
              {METHODS.map((m) => (
                <button key={m.id} onClick={() => setMethod(m.id)} type="button" data-testid={`receipt-method-${m.id}`}
                  className={`rounded-2xl py-3 text-sm font-semibold border transition-all duration-200 ${
                    method === m.id
                      ? "border-[var(--cc-accent)] bg-[var(--cc-accent)]/12 text-[var(--cc-accent)] shadow-inner"
                      : "border-border hover:border-foreground/30"}`}>
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {/* Amount + kind row */}
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2 space-y-1.5">
              <p className="text-xs font-mono-cc uppercase tracking-widest text-muted-foreground">Amount</p>
              <div className="relative">
                <DollarSign className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00"
                  data-testid="receipt-amount-input" className="pl-8 h-12 rounded-2xl font-heading font-bold text-lg" />
              </div>
            </div>
            <div className="space-y-1.5">
              <p className="text-xs font-mono-cc uppercase tracking-widest text-muted-foreground">Kind</p>
              <div className="flex rounded-2xl border p-1 bg-secondary h-12">
                {[["full", "Full"], ["deposit", "Deposit"]].map(([k, l]) => (
                  <button key={k} onClick={() => setKind(k)} type="button" data-testid={`receipt-kind-${k}`}
                    className={`flex-1 text-xs font-bold rounded-xl transition-colors ${
                      kind === k ? "bg-[var(--cc-accent)] text-black" : "text-muted-foreground"}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Optional note */}
          <Input value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Note (optional) — e.g. txn ref"
            data-testid="receipt-note-input"
            className="h-11 rounded-2xl" />

          {/* Confirm — big iOS pill */}
          <Button onClick={submit} disabled={busy || !photo}
            data-testid="receipt-submit"
            className="w-full h-14 rounded-full text-base font-bold bg-[var(--cc-accent)] text-black hover:bg-[var(--cc-accent)]/90 btn-glow">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> :
             <><Upload className="w-4 h-4 mr-2" /> Mark Paid {amount ? "· " + fmtMoney(amount) : ""}</>}
          </Button>
          <p className="text-[11px] text-muted-foreground text-center leading-relaxed">
            Uploads a photo of your Venmo / Zelle / cash / check confirmation and instantly marks the mission paid.
            The receipt stays attached to the job for your records.
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
